import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getRooms, subscribeToInbox, leaveRoom } from '../../services/chat';
import ChatRoomListItem from './ChatRoomListItem';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { BOTTOM_NAVIGATION_VISIBILITY } from '../../constants/actionTypes';

const ChatInboxView = ({ currentUser, setBottomNavigationVisible }) => {
  const navigate = useNavigate();
  const currentUsername = currentUser?.username || '';

  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const debounceRef = useRef(null);

  const fetchRooms = useCallback(async () => {
    if (!currentUsername) return;
    const data = await getRooms(currentUsername);
    setRooms(data);
  }, [currentUsername]);

  useEffect(() => {
    setBottomNavigationVisible(false);
    fetchRooms().finally(() => setIsLoading(false));
    return () => setBottomNavigationVisible(true);
  }, []);

  useEffect(() => {
    const sub = subscribeToInbox(() => fetchRooms());
    return () => { sub.unsubscribe(); };
  }, [fetchRooms]);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(val), 300);
  };

  const filteredRooms = query
    ? rooms.filter(r => r.name?.toLowerCase().includes(query.toLowerCase()))
    : rooms;

  return (
    <Box
      sx={{
        height: '100dvh',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Fixed header — never scrolls away */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          pt: 'calc(12px + env(safe-area-inset-top))',
          pb: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: 'rgba(13,27,42,0.95)',
        }}
      >
        <IconButton onClick={() => navigate('/freak')} sx={{ color: 'white' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
          Messages
        </Typography>
      </Box>

      {/* Search — fixed below header */}
      <Box sx={{ flexShrink: 0, px: 2, pt: 1, pb: 1, backgroundColor: 'rgba(13,27,42,0.6)' }}>
        <TextField
          fullWidth
          placeholder="Search conversations…"
          onChange={handleQueryChange}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderRadius: 3,
              '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
            },
            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.35)' },
          }}
        />
      </Box>

      {/* Scrollable room list — takes remaining height */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6, flex: 1 }}>
          <CircularProgress sx={{ color: DEFAULT_THEME_COLOR }} />
        </Box>
      ) : filteredRooms.length === 0 ? (
        <Box sx={{ textAlign: 'center', pt: 6, flex: 1 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {query ? 'No conversations match your search.' : 'No conversations yet.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 1,
            // leave room for the FAB + home indicator
            pb: 'calc(88px + env(safe-area-inset-bottom))',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {filteredRooms.map(room => (
            <ChatRoomListItem
              key={room.id}
              room={room}
              onPress={() => navigate(`/chat/${room.id}`, { state: { roomName: room.name } })}
            />
          ))}
        </Box>
      )}

      <Fab
        onClick={() => navigate('/chat/new')}
        sx={{
          position: 'fixed',
          bottom: 'calc(24px + env(safe-area-inset-bottom))',
          right: 24,
          backgroundColor: DEFAULT_THEME_COLOR,
          color: 'white',
          '&:hover': { backgroundColor: '#2aad8a' },
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}
      >
        <EditIcon />
      </Fab>
    </Box>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = dispatch => ({
  setBottomNavigationVisible: isVisible => dispatch({ type: BOTTOM_NAVIGATION_VISIBILITY, payload: isVisible }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatInboxView);
